import { useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"
import { isMatch, parse, format } from "date-fns"
import classNames from "classnames"

import type { TurnstileInstance } from "@marsidev/react-turnstile"
import { Turnstile } from "@marsidev/react-turnstile"

import { MdVisibility, MdVisibilityOff } from "react-icons/md"

import Info from "./assets/info.svg?react"
import Loading from "./assets/loading.gif"

import { GiftCardWalletLinks } from "./GiftCardWalletLinks"

import {
  apiSlice,
} from "../../app/apiSlice"

import {
  useGetGiftCardMutation,
} from "./giftCardApiSlice"

export const getGiftCardDetailSchema = yup.object({
  number: yup
    .string()
    .required(),
  pin: yup
    .string()
    .required(),
})

export const GiftCardDetail = () => {
  const { t } = useTranslation("gift_card")

  const {
    data: configData,
  } = apiSlice.endpoints.getConfig.useQueryState()

  const [
    getGiftCard,
    {
      isLoading: giftCardIsLoading,
      data: giftCardData,
      error: giftCardError,
    },
  ] = useGetGiftCardMutation()

  const { handleSubmit, register, formState: { errors }, setError } = useForm({
    resolver: yupResolver(getGiftCardDetailSchema),
    values: {
      number: "",
      pin: "",
    },
  })

  const turnstileRef = useRef<TurnstileInstance | null>(null)

  const onSubmit = (data: any) => {
    const validation_token = turnstileRef.current?.getResponse()

    getGiftCard({
      ...data,
      validation_token,
    })
      .finally(() => {
        turnstileRef.current?.reset()
      })
  }

  useEffect(() => {
    if (!giftCardError) return;
    setError("number", {})
    setError("pin", {})
  }, [ setError, giftCardError ])

  const [ showPin, setShowPin ] = useState<boolean>(false)

  return (
    <article className="my-auto">
      <h2 className="lg:mx-auto lg:max-w-113 mb-6 font-semibold text-[2rem] leading-none">
        {t("niki.gift_card.gift_card_detail.title")}
      </h2>
      <form className="lg:mx-auto lg:w-113 mb-8 grid grid-cols-12 gap-4"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        autoComplete="off"
      >
        <div className="col-span-12 flex flex-col gap-1">
          <label
            htmlFor="number"
          >
            {t("niki.gift_card.get_gift_card.number")}
          </label>
          <input className={classNames("input input-fullwidth input-primary", { "input-error": !!errors["number"] })}
            id="number"
            type="text"
            {...register("number")}
          />
        </div>
        <div className="col-span-12 flex flex-col gap-1">
          <label
            htmlFor="pin"
          >
            {t("niki.gift_card.get_gift_card.pin")}
          </label>
          <div className="relative">
            <input className={classNames("input input-fullwidth input-primary pr-21", { "input-error": !!errors["pin"] })}
              id="pin"
              type={showPin ? "text" : "password"}
              {...register("pin")}
            />
            <button className="absolute top-1/2 -translate-y-1/2 right-12 flex justify-center items-center size-6 text-[#BBBFCA] text-[1.5rem]"
              type="button"
              onClick={() => {
                setShowPin(!showPin)
              }}
            >
              {showPin ? <MdVisibilityOff /> : <MdVisibility />}
            </button>
            <button className="absolute top-1/2 -translate-y-1/2 right-3 flex justify-center items-center size-6 text-[#BBBFCA] text-[1.5rem]"
              type="button"
              onClick={() => {
                withReactContent(Swal).fire({
                  customClass: {
                    container: "popup-container",
                    popup: "popup w-[494px]!",
                    closeButton: "popup-close",
                    icon: "popup-icon popup-icon-primary",
                    title: "popup-title text-lg! leading-8!",
                    htmlContainer: "popup-text text-lg! leading-[1.4375rem]!",
                    actions: "popup-actions",
                    confirmButton: "col-span-2! popup-action popup-action-primary",
                  },
                  heightAuto: false,
                  showCloseButton: true,
                  title: t("niki.gift_card.get_gift_card.pin.title"),
                  html: (
                    <>
                      <div className="flex justify-center items-center mb-4 text-[3rem] leading-none">
                        <Info />
                      </div>
                      <div>
                        {t("niki.gift_card.get_gift_card.pin.content")}
                      </div>
                    </>
                  ),
                  showConfirmButton: true,
                })
              }}
            >
              <Info />
            </button>
          </div>
        </div>
        <div className="col-span-12">
          <Turnstile
            ref={turnstileRef}
            siteKey={JSON.parse(import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY_BY_TENANT || "{}")[configData?.tenant]}
            options={{
              size: "flexible",
              theme: "light",
            }}
          />
        </div>
        <button className={classNames("col-span-12 button button-fullwidth button-primary", { "button-secondary": !!giftCardData })}
          type="submit"
        >
          {t("niki.gift_card.get_gift_card.submit")}
        </button>
      </form>
      {!!giftCardData && configData?.digital_wallets_enabled && (
        <GiftCardWalletLinks
          appleWalletLink={giftCardData?.data?.apple_wallet_pass_url}
          googleWalletLink={giftCardData?.data?.android_wallet_pass_url}
        />
      )}
      {!!giftCardData && (
        <div className="lg:mx-auto lg:max-w-232 flex flex-col gap-2 lg:gap-6 rounded-2xl lg:rounded-4xl p-2 lg:p-8 shadow-card-shadow">
          <div className="grid grid-cols-12">
            <div className="col-span-4 flex flex-col gap-3 lg:gap-6">
              <div className="text-sm lg:text-base lg:leading-5 text-center text-truncate">
                {t("niki.gift_card.gift_card.balance")}
              </div>
              <div className={classNames("font-semibold text-base lg:text-[2rem] leading-11 text-center text-truncate", { "text-[#BBBFCA]": !giftCardData })}>
                {giftCardData?.data?.current_balance
                  ? (
                    <>
                      {configData?.currency?.symbolLeft || null}
                      {giftCardData?.data?.current_balance?.toFixed(2)}
                      {configData?.currency?.symbolRight || null}
                    </>
                  )
                  : "-"
                }
              </div>
            </div>
            <div className="col-span-4 flex flex-col gap-3 lg:gap-6">
              <div className="text-sm lg:text-base lg:leading-5 text-center text-truncate">
                {t("niki.gift_card.gift_card.status")}
              </div>
              <div className={classNames("font-semibold text-base lg:text-[2rem] leading-11 text-center text-truncate", { "text-[#BBBFCA]": !giftCardData })}>
                {giftCardData?.data?.status?.data?.matchcode || "-"}
              </div>
            </div>
            <div className="col-span-4 flex flex-col gap-3 lg:gap-6">
              <div className="text-sm lg:text-base lg:leading-5 text-center text-truncate">
                {t("niki.gift_card.gift_card.validation_date")}
              </div>
              <div className={classNames("font-semibold text-base lg:text-[2rem] leading-11 text-center text-truncate", { "text-[#BBBFCA]": !giftCardData })}>
                {(giftCardData?.data?.valid_to && isMatch(giftCardData.data.valid_to, "dd-MM-yyyy")) ? format(parse(giftCardData?.data?.valid_to, "dd-MM-yyyy", new Date()) ,"dd.MM.yyyy") : "-"}
              </div>
            </div>
          </div>
          <hr className="border-b-[#BBBFCA]" />
          <table className="table-fixed w-full max-w-full">
            <thead>
              <tr>
                <th className="w-[23%] max-w-[20%] py-1.5 lg:py-2 px-2 lg:px-6 bg-[#0D0D0D] text-[#FFFFFF] font-semibold text-sm text-center text-truncate rounded-tl-2xl rounded-bl-2xl">
                  {t("niki.gift_card.gift_card_logs.date")}
                </th>
                <th className="w-[20%] max-w-[20%] py-1.5 lg:py-2 px-2 lg:px-6 bg-[#0D0D0D] text-[#FFFFFF] font-semibold text-sm text-center text-truncate">
                  {t("niki.gift_card.gift_card_logs.shop")}
                </th>
                <th className="w-[17%] max-w-[20%] py-1.5 lg:py-2 px-2 lg:px-6 bg-[#0D0D0D] text-[#FFFFFF] font-semibold text-sm text-center text-truncate">
                  {t("niki.gift_card.gift_card_logs.document")}
                </th>
                <th className="w-[20%] max-w-[20%] py-1.5 lg:py-2 px-2 lg:px-6 bg-[#0D0D0D] text-[#FFFFFF] font-semibold text-sm text-center text-truncate">
                  {t("niki.gift_card.gift_card_logs.used")}
                </th>
                <th className="w-[20%] max-w-[20%] py-1.5 lg:py-2 px-2 lg:px-6 bg-[#0D0D0D] text-[#FFFFFF] font-semibold text-sm text-center text-truncate rounded-tr-2xl rounded-br-2xl">
                  {t("niki.gift_card.gift_card_logs.available")}
                </th>
              </tr>
            </thead>
            <tbody>
              {!giftCardData?.data?.giftCardLogs?.data?.length
                ? (
                  <tr className="[&:first-child>td]:pt-1.5! [&:last-child>td]:pb-1.5! lg:[&:first-child>td]:pt-2! lg:[&:last-child>td]:pb-2!">
                    <td className="w-[23%] max-w-[20%] py-[.185rem] lg:py-1 px-2 lg:px-6 text-[#BBBFCA] font-semibold lg:font-normal text-base text-center">
                      -
                    </td>
                    <td className="w-[20%] max-w-[20%] py-[.185rem] lg:py-1 px-2 lg:px-6 text-[#BBBFCA] font-semibold lg:font-normal text-base text-center">
                      -
                    </td>
                    <td className="w-[17%] max-w-[20%] py-[.185rem] lg:py-1 px-2 lg:px-6 text-[#BBBFCA] font-semibold lg:font-normal text-base text-center">
                      -
                    </td>
                    <td className="w-[20%] max-w-[20%] py-[.185rem] lg:py-1 px-2 lg:px-6 text-[#BBBFCA] font-semibold lg:font-normal text-base text-center">
                      -
                    </td>
                    <td className="w-[20%] max-w-[20%] py-[.185rem] lg:py-1 px-2 lg:px-6 text-[#BBBFCA] font-semibold lg:font-normal text-base text-center">
                      -
                    </td>
                  </tr>
                )
                : null
              }
              {giftCardData?.data?.giftCardLogs?.data?.map(({
                id,
                created_at,
                filial,
                doc_key,
                doc_serial,
                doc_number,
                debit_value,
                current_balance,
              }: any) => (
                <tr key={id} className="[&:first-child>td]:pt-1.5! [&:last-child>td]:pb-1.5! lg:[&:first-child>td]:pt-2! lg:[&:last-child>td]:pb-2!">
                  <td className="w-[20%] max-w-[20%] py-[.185rem] lg:py-1 px-2 lg:px-6 font-semibold lg:font-normal text-base text-center">
                    {(created_at && isMatch(created_at, "dd-MM-yyyy HH:mm:ss")) ? format(parse(created_at, "dd-MM-yyyy HH:mm:ss", new Date()), "dd-MM-yyyy HH:mm") : "-"}
                  </td>
                  <td className="w-[20%] max-w-[20%] py-[.185rem] lg:py-1 px-2 lg:px-6 font-semibold lg:font-normal text-base text-center">
                    {filial?.data?.matchcode || "-"}
                  </td>
                  <td className="w-[20%] max-w-[20%] py-[.185rem] lg:py-1 px-2 lg:px-6 font-semibold lg:font-normal text-base text-center">
                    {!(!doc_key && !doc_serial && !doc_number)
                      ? (
                        <>
                          {doc_key || null}
                          {(doc_key && doc_serial) ? "/" : null}
                          {doc_serial || null}
                          {(doc_key && doc_serial) ? "-" : null}
                          {doc_number || null}
                        </>
                      )
                      : "-"
                    }
                  </td>
                  <td className="w-[20%] max-w-[20%] py-[.185rem] lg:py-1 px-2 lg:px-6 font-semibold lg:font-normal text-base text-center">
                    {debit_value && typeof debit_value === "number"
                      ? (
                        <>
                          {configData?.currency?.symbolLeft || null}
                          {debit_value.toFixed(2)}
                          {configData?.currency?.symbolRight || null}
                        </>
                      )
                      : "-"
                    }
                  </td>
                  <td className="w-[20%] max-w-[20%] py-[.185rem] lg:py-1 px-2 lg:px-6 font-semibold lg:font-normal text-base text-center">
                    {current_balance && typeof current_balance === "number"
                      ? (
                        <>
                          {configData?.currency?.symbolLeft || null}
                          {current_balance.toFixed(2)}
                          {configData?.currency?.symbolRight || null}
                        </>
                      )
                      : "-"
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {giftCardIsLoading
        ? (
          <div className="fixed top-0 bottom-0 left-0 right-0 flex justify-center items-center bg-[#0D0D0D]/10">
            <div className="flex flex-col gap-2.5 py-4 px-6 rounded-lg bg-[#F3F6F8] text-[#495464] lg:ml-118">
              <img className="m-auto size-16"
                src={Loading}
                alt=""
              />
              {t("niki.gift_card.loading")}
            </div>
          </div>
        )
        : null
      }
    </article>
  )
}
